import {
  AhaEditorUtils,
  type PasteTransformPlugin,
} from '@aha-app/aha-editor-v2';
import { showAlertMessage } from 'javascripts/messages';
import { sanitizeHtml } from 'javascripts/utils';

// Add the attachment thumbnail and the attachment hidden field to the
// editor wrapper, so they can be associated when the record is
// created. This is only necessary for new records, existing records
// will be handled through reactive updates.
function setAttachmentThumbnail(
  attachment: { thumbnail_html: string },
  container: HTMLElement
) {
  const attachments = container.querySelector(':scope .attachments__body');
  if (!attachments) return;

  attachments.insertAdjacentHTML(
    'beforeend',
    sanitizeHtml(attachment.thumbnail_html)
  );
}

async function duplicateAttachmentRecord({
  attachmentId,
  baseURL,
  getSharingToken,
  objectType,
  objectId,
  namePrefix,
}: {
  attachmentId: RecordId;
  baseURL: string;
  getSharingToken?: () => string;
  objectType?: string;
  objectId?: string;
  namePrefix?: string;
}) {
  const authorization = getSharingToken && getSharingToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-CSRF-Token': window.csrfToken(),
  };

  if (authorization) {
    headers.Authorization = `Bearer ${authorization}`;
  }

  const response = await fetch(
    `${baseURL}/attachments/${attachmentId}/duplicate`,
    {
      method: 'POST',
      body: JSON.stringify({
        attachable_type: objectType,
        attachable_id: objectId,
        name_prefix: namePrefix,
      }),
      headers: {
        ...headers,
        'Content-Type': 'application/json',
        'X-CSRF-Token': window.csrfToken(),
      },
    }
  );

  return response.json() as Promise<{
    attachment: {
      id: RecordId;
      download_url: string;
      thumbnail_html: string;
    };
  }>;
}

const attachmentPasteTransformerFactory = <
  T extends 'audio' | 'video' | 'image' | 'attachment'
>({
  type,
  namePrefix,
  baseURL = '',
  getSharingToken,
  objectType,
  objectId,
  getEnclosingElement,
}: {
  type: T;
  namePrefix?: string;
  baseURL?: string;
  getSharingToken?: () => string;
  objectType: string;
  objectId?: string;
  getEnclosingElement: () => HTMLElement | undefined;
}) => {
  return {
    name: `${type} attachment paste transformer`,
    pasteTransformer: {
      type,
      transform: (editor, controller, element, isMove) => {
        const attachmentIdMatch =
          element.media_src.match(/attachments\/(\d+)\//);
        const attachmentId = (attachmentIdMatch && attachmentIdMatch[1]) as
          | `${number}`
          | null;
        if (!attachmentId) return element;

        const placeholderId = String(Math.floor(Math.random() * 10000000));

        const attachmentOptions = {
          attachmentId,
          placeholderId,
          namePrefix,
          baseURL,
          getSharingToken,
          objectType,
          objectId,
          enclosingElement: getEnclosingElement(),
          type,
        };

        // Don't need to duplicate if copying media to the same record
        const container = attachmentOptions.enclosingElement;
        const attachmentElement = container?.querySelector(
          `:scope #attachment-${attachmentOptions.attachmentId}`
        );
        if (attachmentElement) return element;

        const existing = AhaEditorUtils.Editor.firstElementWithProperties(
          editor,
          {
            type,
            media_src: element.media_src,
          }
        );
        if (existing) return element;

        duplicateAttachmentRecord({
          attachmentId: attachmentOptions.attachmentId,
          baseURL: attachmentOptions.baseURL,
          getSharingToken: attachmentOptions.getSharingToken,
          objectType: attachmentOptions.objectType,
          objectId: attachmentOptions.objectId,
          namePrefix: attachmentOptions.namePrefix,
        })
          .then(res => {
            const placeholderNode =
              AhaEditorUtils.Editor.firstElementWithProperties(editor, {
                media_src: `placeholder:${placeholderId}`,
              });

            if (placeholderNode) {
              AhaEditorUtils.Transforms.removeNodes(editor, {
                at: placeholderNode[1],
              });
              AhaEditorUtils.Transforms.insertNodes(
                editor,
                {
                  ...element,
                  media_src: res.attachment.download_url,
                },
                { at: placeholderNode[1] }
              );

              if (container && !objectId) {
                setAttachmentThumbnail(res.attachment, container);
              }
            }
          })
          .catch(err => {
            showAlertMessage(err);
            console.error(err);
          });

        return {
          ...element,
          media_src: `placeholder:${placeholderId}`,
        };
      },
    } as PasteTransformPlugin<T>,
  };
};

export default attachmentPasteTransformerFactory;
